import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import DateFilter from "./DateFilter";
import Pagination from "./Pagination";

const TH = ["ID", "Customer", "Email", "Amount", "Status", "Description", "Date", "Receipt"];

const toUnix = (d, endOfDay) => {
  if (!d) return null;
  const dt = new Date(d);
  if (endOfDay) dt.setHours(23, 59, 59, 999);
  return Math.floor(dt.getTime() / 1000);
};

const STATUS = {
  succeeded: { bg: "#f0fdf4", color: "#16a34a" },
  failed:    { bg: "#fef2f2", color: "#dc2626" },
  pending:   { bg: "#fffbeb", color: "#d97706" },
};

function SkeletonRows() {
  return Array.from({ length: 6 }).map((_, i) => (
    <tr key={i}>
      {TH.map((_, j) => (
        <td key={j} style={{ padding: "13px 14px" }}>
          <div className="stripe-skeleton" style={{ height: 13, borderRadius: 4, width: j === 0 ? 80 : "65%" }} />
        </td>
      ))}
    </tr>
  ));
}

export default function TransactionsTab() {
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [cursorStack, setCursorStack] = useState([]);
  const [page, setPage] = useState(1);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const buildUrl = useCallback((cursor, start, end) => {
    let url = "/api/stripe/transactions?limit=50";
    const s = toUnix(start, false);
    const e = toUnix(end, true);
    if (s) url += `&startDate=${s}`;
    if (e) url += `&endDate=${e}`;
    if (cursor) url += `&starting_after=${cursor}`;
    return url;
  }, []);

  const fetchPage = useCallback((cursor, start, end) => {
    setLoading(true);
    axios
      .get(buildUrl(cursor, start, end))
      .then((res) => {
        if (res.data.success) {
          setTxns(res.data.data);
          setHasMore(res.data.hasMore);
        }
      })
      .finally(() => setLoading(false));
  }, [buildUrl]);

  useEffect(() => { fetchPage(undefined, "", ""); }, [fetchPage]);

  const handleApply = () => {
    setCursorStack([]); setPage(1);
    fetchPage(undefined, startDate, endDate);
  };
  const handleClear = () => {
    setStartDate(""); setEndDate("");
    setCursorStack([]); setPage(1);
    fetchPage(undefined, "", "");
  };
  const handleNext = () => {
    const lastId = txns[txns.length - 1]?.id;
    if (!lastId) return;
    setCursorStack((p) => [...p, lastId]);
    setPage((p) => p + 1);
    fetchPage(lastId, startDate, endDate);
  };
  const handlePrev = () => {
    const newStack = cursorStack.slice(0, -1);
    setCursorStack(newStack);
    setPage((p) => p - 1);
    fetchPage(newStack[newStack.length - 1], startDate, endDate);
  };

  return (
    <div className="stripe-section-card">
      <div className="stripe-section-header">
        <div>
          <span className="stripe-section-title">Transactions</span>
          {!loading && <span className="stripe-section-count">{txns.length} on this page</span>}
        </div>
        <DateFilter
          startDate={startDate} endDate={endDate}
          onStartChange={setStartDate} onEndChange={setEndDate}
          onApply={handleApply} onClear={handleClear}
        />
      </div>

      <div className="scroll-x-container" style={{ border: "none", borderRadius: 0 }}>
        <div className="sales-table-wrap">
          <table className="sales-table">
            <thead>
              <tr>{TH.map((h) => <th key={h}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {loading ? <SkeletonRows /> : txns.length === 0 ? (
                <tr>
                  <td colSpan={TH.length}>
                    <div className="stripe-empty">
                      <div className="stripe-empty-icon">💸</div>
                      No transactions found
                    </div>
                  </td>
                </tr>
              ) : txns.map((t) => {
                const st = STATUS[t.status] || { bg: "#f3f4f6", color: "#374151" };
                return (
                  <tr key={t.id}>
                    <td style={{ fontFamily: "monospace", fontSize: 11, color: "#9ca3af" }}>
                      {t.id.slice(0, 14)}…
                    </td>
                    <td style={{ fontWeight: 700 }}>{t.customerName || "—"}</td>
                    <td style={{ color: "#6b7280" }}>{t.customerEmail || "—"}</td>
                    <td style={{ fontWeight: 800 }}>
                      ${t.amount.toFixed(2)}&nbsp;
                      <span style={{ fontWeight: 600, fontSize: 11, color: "#9ca3af" }}>
                        {t.currency?.toUpperCase()}
                      </span>
                    </td>
                    <td>
                      <span className="stripe-badge" style={{ background: st.bg, color: st.color }}>
                        {t.status}
                      </span>
                    </td>
                    <td style={{ color: "#6b7280", maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {t.description || "—"}
                    </td>
                    <td style={{ color: "#6b7280", whiteSpace: "nowrap" }}>
                      {new Date(t.createdAt * 1000).toLocaleDateString()}
                    </td>
                    <td>
                      {t.receiptUrl
                        ? <a href={t.receiptUrl} target="_blank" rel="noreferrer" className="stripe-link">Receipt ↗</a>
                        : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <Pagination page={page} hasMore={hasMore} onPrev={handlePrev} onNext={handleNext} loading={loading} />
    </div>
  );
}
